import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicBookingService } from '../../services/api';
import { formatApptTime, localDateKey } from '../../utils/dateFormatters';
import './Booking.css';

const LOCATION_LABEL = { in_person: 'In person', phone: 'Phone call', video: 'Video call', client_location: 'At your place' };
const STEPS = ['Person', 'Meeting', 'Date', 'Time', 'Details'];

function dateLabel(key) {
  return new Date(key + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

export default function BookingWizard() {
  const { staffSlug: slugParam } = useParams();
  const [step, setStep] = useState(0);
  const [staffList, setStaffList] = useState([]);
  const [staff, setStaff] = useState(null); // { slug, name, color }
  const [services, setServices] = useState([]);
  const [service, setService] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [details, setDetails] = useState({ name: '', email: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // booking result

  // Load people (or jump straight in on a deep link)
  useEffect(() => {
    publicBookingService.listStaff().then((r) => { if (r.success) setStaffList(r.staff || []); });
  }, []);
  useEffect(() => {
    if (slugParam) { selectStaff({ slug: slugParam }, true); }
  }, [slugParam]);

  async function selectStaff(s, deepLink = false) {
    setError('');
    const r = await publicBookingService.getStaffServices(s.slug);
    if (!r.success) { setError('That person is not available for booking.'); return; }
    setStaff(r.staff);
    setServices(r.services || []);
    setStep(1);
  }

  async function selectService(svc) {
    setService(svc);
    setSlot(null); setDate(null);
    setStep(2);
    setLoadingSlots(true);
    const r = await publicBookingService.getSlots(svc.id);
    setSlots(r.success ? (r.slots || []) : []);
    setLoadingSlots(false);
  }

  const slotsByDate = useMemo(() => {
    const map = {};
    for (const s of slots) { const k = localDateKey(s.start); (map[k] ||= []).push(s); }
    return map;
  }, [slots]);
  const availableDates = useMemo(() => Object.keys(slotsByDate).sort(), [slotsByDate]);

  async function confirm() {
    setError('');
    if (!details.name.trim()) { setError('Please enter your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email)) { setError('Please enter a valid email.'); return; }
    setSubmitting(true);
    const r = await publicBookingService.createBooking({
      serviceId: service.id, startsAt: slot.start,
      name: details.name.trim(), email: details.email.trim(), phone: details.phone.trim(), notes: details.notes.trim()
    });
    setSubmitting(false);
    if (r.success) setDone(r.booking);
    else setError(r.error || 'Sorry, that time is no longer available. Please pick another.');
  }

  function back() { setError(''); setStep((s) => Math.max(slugParam ? 1 : 0, s - 1)); }

  // ---- Done screen ----
  if (done) {
    return (
      <div className="booking-card booking-done">
        <div className="done-check">✓</div>
        <h1>You're booked in!</h1>
        <p className="text-muted">{service.name} with {staff.name}</p>
        <p className="done-when">{new Date(done.starts_at).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit', hour12: true })}</p>
        <p className="text-muted">A confirmation has been sent to {details.email}.</p>
        <div className="done-actions">
          <Link className="btn btn-outline" to={`/booking/${done.manage_token}`}>Manage booking</Link>
          <Link className="btn btn-primary" to="/book" onClick={() => window.location.reload()}>Book another</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-wizard">
      <div className="stepper">
        {STEPS.map((label, i) => (
          <div key={label} className={`step-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
            <span>{i + 1}</span><label>{label}</label>
          </div>
        ))}
      </div>

      {error && <div className="booking-error">{error}</div>}

      <div className="booking-card">
        {step === 0 && (
          <>
            <h1>Who would you like to meet with?</h1>
            <div className="choice-list">
              {staffList.map((s) => (
                <button key={s.slug} className="choice" onClick={() => selectStaff(s)}>
                  <span className="choice-avatar" style={{ background: s.color || 'var(--color-teal)' }}>{s.name[0]}</span>
                  <span className="choice-body"><strong>{s.name}</strong>{s.bio && <span className="text-muted">{s.bio}</span>}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <h1>Choose a meeting type</h1>
            <p className="text-muted booking-sub">with {staff?.name}</p>
            <div className="choice-list">
              {services.length === 0 && <p className="text-muted">No meeting types available right now.</p>}
              {services.map((svc) => (
                <button key={svc.id} className="choice" onClick={() => selectService(svc)}>
                  <span className="choice-body">
                    <strong>{svc.name}</strong>
                    {svc.description && <span className="text-muted">{svc.description}</span>}
                    <span className="choice-meta">
                      <span className="chip">{svc.duration_minutes} min</span>
                      <span className="chip">{LOCATION_LABEL[svc.location_mode] || svc.location_mode}</span>
                      {svc.price ? <span className="chip">${svc.price}</span> : null}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h1>Pick a day</h1>
            <p className="text-muted booking-sub">{service?.name} · {service?.duration_minutes} min</p>
            {loadingSlots ? <div className="loading-spinner"></div>
              : availableDates.length === 0 ? <p className="text-muted">No availability in the booking window. Please check back later.</p>
              : (
                <div className="date-grid">
                  {availableDates.map((k) => (
                    <button key={k} className={`date-chip ${date === k ? 'active' : ''}`} onClick={() => { setDate(k); setStep(3); }}>
                      {dateLabel(k)}
                    </button>
                  ))}
                </div>
              )}
          </>
        )}

        {step === 3 && (
          <>
            <h1>Pick a time</h1>
            <p className="text-muted booking-sub">{date && dateLabel(date)}</p>
            <div className="time-grid">
              {(slotsByDate[date] || []).map((s) => (
                <button key={s.start} className={`time-chip ${slot?.start === s.start ? 'active' : ''}`} onClick={() => { setSlot(s); setStep(4); }}>
                  {formatApptTime(s.start)}
                </button>
              ))}
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <h1>Your details</h1>
            <p className="text-muted booking-sub">{service?.name} with {staff?.name} · {date && dateLabel(date)} at {slot && formatApptTime(slot.start)}</p>
            <div className="form-group"><label htmlFor="name">Name *</label>
              <input id="name" value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} autoFocus /></div>
            <div className="form-group"><label htmlFor="email">Email *</label>
              <input id="email" type="email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} /></div>
            <div className="form-group"><label htmlFor="phone">Phone</label>
              <input id="phone" type="tel" value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} /></div>
            <div className="form-group"><label htmlFor="notes">Anything we should know?</label>
              <textarea id="notes" rows="3" value={details.notes} onChange={(e) => setDetails({ ...details, notes: e.target.value })} /></div>
          </>
        )}
      </div>

      <div className="booking-actions">
        {step > (slugParam ? 1 : 0) && <button className="btn btn-outline" onClick={back}>Back</button>}
        {step === 4 && (
          <button className="btn btn-primary booking-confirm" onClick={confirm} disabled={submitting}>
            {submitting ? 'Booking…' : 'Confirm booking'}
          </button>
        )}
      </div>
    </div>
  );
}
